import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../cn";

export interface NumberedSectionProps extends Omit<HTMLAttributes<HTMLElement>, "title"> {
  /** The step's position — drawn in the circle unless `done`. */
  number: number;
  done?: boolean;
  title: ReactNode;
  /** A status chip or similar, right-aligned beside the title. */
  aside?: ReactNode;
  /** Lower opacity for a step that depends on one earlier — still readable. */
  dimmed?: boolean;
  children?: ReactNode;
}

/**
 * One numbered step of a sheet that walks through a sequence — Connect, add an
 * API key, switch it on. Shares the numbered-circle vocabulary `Steps` uses for
 * tabs, but this is prose inside a sheet rather than navigation, so it is a
 * `section` with a rule above it instead of a tablist.
 *
 * `dimmed` is not `disabled`: a step two steps away is still worth reading
 * before you reach it, so this only lowers opacity and never `pointer-events`
 * or `aria-disabled`.
 */
export function NumberedSection({
  number,
  done = false,
  title,
  aside,
  dimmed = false,
  children,
  className,
  ...props
}: NumberedSectionProps) {
  return (
    <section
      className={cn(
        "flex flex-col gap-2 border-t border-rule pt-4 first:border-t-0 first:pt-0",
        className,
      )}
      {...props}
    >
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className={cn(
            "grid size-5 shrink-0 place-items-center rounded-full text-micro font-semibold",
            done ? "bg-positive-soft text-positive-text" : "bg-surface-2 text-ink-2",
          )}
        >
          {done ? "✓" : number}
        </span>
        <span
          className={cn(
            "min-w-0 grow font-sans text-ui font-semibold",
            dimmed ? "text-ink-3" : "text-ink",
          )}
        >
          {title}
        </span>
        {aside ? <span className="shrink-0">{aside}</span> : null}
      </div>
      {children ? <div className="pl-7.5">{children}</div> : null}
    </section>
  );
}
